if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      constructor() {
        super();
        this.abortController = null;
      }

      connectedCallback() {
        // Prevent change and input events from bubbling up to parent cart-drawer-items
        this.addEventListener('change', (e) => e.stopPropagation());
        this.addEventListener('input', (e) => e.stopPropagation());

        this.loadRecommendations();
        this.initEventListeners();
      }

      loadRecommendations() {
        const url = this.dataset.url;
        const productId = this.dataset.productId;
        if (!url) return;

        // Prevent redundant fetch if already loaded for the current product
        if (this.dataset.loadedProductId === productId && this.querySelector('.cart-recommendations__grid')) {
          this.initEventListeners();
          return;
        }

        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();

        const fetchUrl = productId ? `${url}&product_id=${productId}` : url;

        fetch(fetchUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            const recommendations = html.querySelector('.cart-recommendations');
            if (recommendations && recommendations.innerHTML.trim().length > 0) {
              this.innerHTML = recommendations.innerHTML;
              this.dataset.loadedProductId = productId || '';
              this.classList.remove('hidden');
              this.initEventListeners();
            } else {
              this.classList.add('hidden');
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error('Error loading cart recommendations:', error);
              this.classList.add('hidden');
            }
          });
      }

      initEventListeners() {
        const grid = this.querySelector('.cart-recommendations__grid');
        const prevBtn = this.querySelector('.cart-recommendations__nav-btn--prev');
        const nextBtn = this.querySelector('.cart-recommendations__nav-btn--next');

        // Nav buttons & scrolling
        if (grid) {
          const updateNavButtons = () => {
            if (!prevBtn || !nextBtn) return;
            const maxScroll = grid.scrollWidth - grid.clientWidth;
            prevBtn.disabled = grid.scrollLeft <= 5;
            nextBtn.disabled = grid.scrollLeft >= maxScroll - 5;
          };

          if (prevBtn && !prevBtn.dataset.bound) {
            prevBtn.dataset.bound = 'true';
            prevBtn.addEventListener('click', (e) => {
              e.preventDefault();
              const card = grid.querySelector('.cart-recommendations__card');
              const step = card ? card.offsetWidth + 12 : 172;
              grid.scrollBy({ left: -step, behavior: 'smooth' });
            });
          }

          if (nextBtn && !nextBtn.dataset.bound) {
            nextBtn.dataset.bound = 'true';
            nextBtn.addEventListener('click', (e) => {
              e.preventDefault();
              const card = grid.querySelector('.cart-recommendations__card');
              const step = card ? card.offsetWidth + 12 : 172;
              grid.scrollBy({ left: step, behavior: 'smooth' });
            });
          }

          if (!grid.dataset.scrollBound) {
            grid.dataset.scrollBound = 'true';
            grid.addEventListener('scroll', updateNavButtons, { passive: true });
            setTimeout(updateNavButtons, 100);

            // Mouse drag to scroll
            let isDown = false;
            let startX = 0;
            let scrollLeft = 0;

            grid.addEventListener('mousedown', (e) => {
              if (e.target.closest('button, select, a')) return;
              isDown = true;
              grid.classList.add('is-dragging');
              startX = e.pageX - grid.offsetLeft;
              scrollLeft = grid.scrollLeft;
            });

            const stopDrag = () => {
              if (!isDown) return;
              isDown = false;
              grid.classList.remove('is-dragging');
            };

            window.addEventListener('mouseup', stopDrag);
            grid.addEventListener('mouseleave', stopDrag);

            grid.addEventListener('mousemove', (e) => {
              if (!isDown) return;
              e.preventDefault();
              const x = e.pageX - grid.offsetLeft;
              const walk = (x - startX) * 1.5;
              grid.scrollLeft = scrollLeft - walk;
            });
          }
        }

        // Variant dropdown change listener
        this.querySelectorAll('.cart-recommendations__variant-select').forEach((select) => {
          if (select.dataset.bound) return;
          select.dataset.bound = 'true';
          select.addEventListener('change', (e) => {
            e.stopPropagation();
            const card = e.target.closest('.cart-recommendations__card');
            if (!card) return;
            const addBtn = card.querySelector('.cart-recommendations__add-btn');
            const priceEl = card.querySelector('.cart-recommendations__price');
            const comparePriceEl = card.querySelector('.cart-recommendations__compare-price');
            const selectedOption = e.target.options[e.target.selectedIndex];

            if (addBtn) {
              addBtn.dataset.variantId = e.target.value;
              const isAvailable = !selectedOption.disabled;
              addBtn.disabled = !isAvailable;
              const btnText = addBtn.querySelector('.cart-recommendations__btn-text');
              if (btnText) {
                btnText.textContent = isAvailable ? '+ Add' : 'Sold out';
              }
            }

            if (priceEl && selectedOption.dataset.price) {
              priceEl.textContent = selectedOption.dataset.price;
            }

            if (comparePriceEl) {
              if (selectedOption.dataset.compareAtPrice) {
                comparePriceEl.textContent = selectedOption.dataset.compareAtPrice;
                comparePriceEl.classList.remove('hidden');
              } else {
                comparePriceEl.classList.add('hidden');
              }
            }
          });
        });

        // Add to cart button listener
        this.querySelectorAll('.cart-recommendations__add-btn').forEach((button) => {
          if (button.dataset.bound) return;
          button.dataset.bound = 'true';
          button.addEventListener('click', (e) => {
            e.preventDefault();
            const variantId = button.dataset.variantId;
            if (!variantId || button.disabled) return;
            this.addToCart(button, variantId);
          });
        });
      }

      addToCart(button, variantId) {
        button.disabled = true;
        const spinner = button.querySelector('.loading__spinner');
        const btnText = button.querySelector('.cart-recommendations__btn-text');
        if (spinner) spinner.classList.remove('hidden');
        if (btnText) btnText.style.opacity = '0';

        const body = JSON.stringify({
          id: variantId,
          quantity: 1,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        });

        const config = typeof fetchConfig === 'function' ? fetchConfig() : {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        };

        fetch(routes.cart_add_url, {
          ...config,
          body,
        })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.status && parsedState.status >= 400) {
              alert(parsedState.description || 'Could not add item to cart');
              return;
            }

            // Render updated cart drawer
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(parsedState);
            }

            // Notify cart listeners for progress bar and coupon recalculations
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-recommendations',
                cartData: parsedState,
                variantId: variantId,
              });
            }
          })
          .catch((error) => {
            console.error('Error adding recommended product to cart:', error);
          })
          .finally(() => {
            if (spinner) spinner.classList.add('hidden');
            if (btnText) btnText.style.opacity = '1';
            button.disabled = false;
          });
      }
    }
  );
}
